import React, { useMemo, Component } from 'react';
import { motion } from 'framer-motion';
import { Calculator, Languages, Globe } from 'lucide-react';
import { AppState } from '../../app/types';
import { GroupId } from '../../config/systemConfig';
import { MODULES } from '../../content';
import type { ModuleId, ModuleData } from '../../content';
import { getFamiliesForModule } from '../../content';
import { useAppStore } from '../../app/hooks/useAppStore';
import { DjayIdentity, useAchievementCheckData } from '../../mascot';
import { useVariantUnlocks } from '../../mascot/useVariantUnlocks';
import { useDjayCustomization } from '../../mascot/useDjayCustomization';
import { PageContainer } from '../shared/PageContainer';
import { itemVariants } from '../shared/animations';
import { getGrowthStage, getFamilyMasteryKey } from '../../mastery';
import type { AssessmentReport } from '../../assessment';
import { getRecentMilestones } from '../../utils/activityHelpers';
// Journey Components
import { JourneyTrailhead } from './journey/JourneyTrailhead';
import { TrailStop } from './journey/TrailStop';
import { TrailConnector } from './journey/TrailConnector';
import { CheckpointFlag } from './journey/CheckpointFlag';
import { CollectibleCheckpoint, CollectibleNodeData } from './journey/CollectibleCheckpoint';
import { TreasureChest } from './journey/TreasureChest';
import { JourneyEmptyState } from './journey/JourneyEmptyState';
import { GhostTrophy } from './journey/GhostTrophy';
const SUBJECT_GROUPS: {
  label: string;
  icon: React.ElementType;
  accentColor: string;
  moduleIds: ModuleId[];
  djayMessage: string;
}[] = [{
  label: 'Rekenen',
  icon: Calculator,
  accentColor: 'bg-blue-100 text-blue-600',
  moduleIds: ['Getalbegrip', 'Basisrekenen', 'Metriek', 'Tijd', 'Geld'],
  djayMessage: 'Kijk hoe goed je kan rekenen! 🧮'
}, {
  label: 'Taal',
  icon: Languages,
  accentColor: 'bg-rose-100 text-rose-600',
  moduleIds: ['SpellingWW', 'SpellingRegel', 'Taalbegrip'],
  djayMessage: 'Jouw taal-skills groeien! 📝'
}, {
  label: 'Wereld & Studie',
  icon: Globe,
  accentColor: 'bg-emerald-100 text-emerald-600',
  moduleIds: ['Studievaardig', 'Topo'],
  djayMessage: 'Je ontdekt de wereld! 🌍'
}];
interface ProgressPageProps {
  state: AppState;
  selectedGroup: GroupId;
  onViewReport: (report: AssessmentReport) => void;
  onNavigate: (view: string) => void;
}
export function ProgressPage({
  state,
  selectedGroup,
  onViewReport,
  onNavigate
}: ProgressPageProps) {
  const {
    getModuleMasteryForGroup,
    getAnsweredQuestions
  } = useAppStore();
  const currentStreak = state.dailyStreak?.currentWeekDays || 0;
  const records = useMemo(() => {
    let fastestTime = Infinity;
    let totalCorrect = 0;
    let totalAttempts = 0;
    if (state.questionStates) {
      Object.values(state.questionStates).forEach(qs => {
        qs.history?.forEach(attempt => {
          totalAttempts++;
          if (attempt.isCorrect) {
            totalCorrect++;
            if (attempt.responseTimeMs && attempt.responseTimeMs > 500) {
              fastestTime = Math.min(fastestTime, attempt.responseTimeMs);
            }
          }
        });
      });
    }
    return {
      fastestTime: fastestTime === Infinity ? null : fastestTime,
      accuracy: totalAttempts > 0 ? Math.round(totalCorrect / totalAttempts * 100) : 0,
      longestStreak: state.dailyStreak?.longestWeekStreak || 0,
      perfectSessions: state.dailyStreak?.totalPerfectSessions || 0,
      dailyGoals: state.dailyStreak?.totalDailyGoalsReached || 0
    };
  }, [state.questionStates, state.dailyStreak]);
  // Modules with mastery data
  const activeModules = useMemo(() => {
    return MODULES.filter(module => {
      const families = getFamiliesForModule(module.id);
      return families.length > 0 && families.some(f => {
        const key = getFamilyMasteryKey(f.familyId, selectedGroup);
        return state.familyMastery?.[key] !== undefined;
      });
    });
  }, [state.familyMastery, selectedGroup]);
  const overallMastery = useMemo(() => {
    if (activeModules.length === 0) return 0;
    let masterySum = 0;
    activeModules.forEach(m => {
      masterySum += getModuleMasteryForGroup(m.id);
    });
    return Math.round(masterySum / activeModules.length);
  }, [activeModules, getModuleMasteryForGroup]);
  const overallGrowthStage = getGrowthStage(overallMastery);
  const milestones = getRecentMilestones(state, selectedGroup);
  // Collectible bridge: surface the NEXT Djay variant the learner is closest
  // to unlocking. Secret/always-on variants are excluded so the teaser always
  // points at a concrete, achievable goal. (`hasDiscoveredLongPress` only
  // gates the secret 'goud' variant, which we exclude here, so `false` is safe.)
  const {
    variants
  } = useVariantUnlocks(false);
  const nextUnlock = useMemo(() => {
    const candidates = variants.filter(v => !v.unlocked && v.rarity !== 'secret' && v.id !== 'default' && v.progress.max > 1);
    if (candidates.length === 0) return null;
    return candidates.sort((a, b) => b.progress.current / b.progress.max - a.progress.current / a.progress.max)[0];
  }, [variants]);
  // Shared collectible-check data — assembled by the mascot-domain selector so
  // this page and CollectionPage read collectible status from one source of
  // truth (no divergence). Replaces the inline copy that used to live here.
  const checkData = useAchievementCheckData(state, getAnsweredQuestions, getModuleMasteryForGroup);
  const unlockedCosmetics = useMemo(() => state.stats.unlockedCosmetics ?? [], [state.stats.unlockedCosmetics]);
  const {
    accessories,
    tiers
  } = useDjayCustomization(checkData, unlockedCosmetics);
  // Already-earned collectibles to celebrate inline along the trail.
  // Defaults that are always-on ('none' accessory, 'rookie' tier) are excluded
  // so every node represents a real, earned reward.
  const unlockedCollectibles = useMemo<CollectibleNodeData[]>(() => {
    const nodes: CollectibleNodeData[] = [];
    accessories.forEach(acc => {
      if (acc.unlocked && acc.id !== 'none') {
        nodes.push({
          kind: 'accessory',
          id: acc.id,
          name: acc.name,
          accessory: acc.id
        });
      }
    });
    tiers.forEach(tier => {
      if (tier.unlocked && tier.id !== 'rookie') {
        nodes.push({
          kind: 'tier',
          id: tier.id,
          name: tier.name,
          tier: tier.id
        });
      }
    });
    return nodes;
  }, [accessories, tiers]);
  // Pre-calculate the trail layout
  const trailNodes: {
    type: 'territory' | 'stop' | 'milestone' | 'collectible';
    data: any;
    align?: 'left' | 'right' | 'center';
  }[] = [];
  let alignToggle = false; // false = left, true = right
  // Mutable queue of earned collectibles, drained as we interleave them so
  // they appear progressively as the learner scrolls the trail.
  const collectibleQueue = [...unlockedCollectibles];
  if (activeModules.length > 0) {
    SUBJECT_GROUPS.forEach(group => {
      const groupModules = group.moduleIds.map(id => activeModules.find(m => m.id === id)).filter((m): m is ModuleData => !!m);
      if (groupModules.length > 0) {
        // Add Territory Marker
        trailNodes.push({
          type: 'territory',
          data: group,
          align: 'center'
        });
        // Add Modules
        groupModules.forEach(module => {
          trailNodes.push({
            type: 'stop',
            data: module,
            align: alignToggle ? 'right' : 'left'
          });
          alignToggle = !alignToggle;
        });
        // Sprinkle a milestone if available and we haven't used them all
        if (milestones.length > 0) {
          const ms = milestones.shift();
          if (ms) {
            trailNodes.push({
              type: 'milestone',
              data: ms,
              align: alignToggle ? 'right' : 'left'
            });
            alignToggle = !alignToggle;
          }
        }
        // Celebrate an earned collectible inline after each group.
        if (collectibleQueue.length > 0) {
          const collectible = collectibleQueue.shift();
          if (collectible) {
            trailNodes.push({
              type: 'collectible',
              data: collectible,
              align: alignToggle ? 'right' : 'left'
            });
            alignToggle = !alignToggle;
          }
        }
      }
    });
    // Any remaining earned collectibles get grouped near the trail's end so
    // nothing the learner has unlocked goes uncelebrated.
    collectibleQueue.forEach(collectible => {
      trailNodes.push({
        type: 'collectible',
        data: collectible,
        align: alignToggle ? 'right' : 'left'
      });
      alignToggle = !alignToggle;
    });
  }
  return <PageContainer className="pb-safe px-0 sm:px-6 bg-[#FAFAF8] min-h-screen" data-id="element-1994" data-mp="36eo0imk1iu0g">
      {activeModules.length === 0 ? <motion.div variants={itemVariants} data-id="element-1995" data-mp="3bqbgb5czxqyi">
          <JourneyEmptyState onNavigate={onNavigate} data-id="element-1996" data-mp="3bpjqyc9zspgo" />
        </motion.div> : <div className="flex flex-col w-full max-w-2xl mx-auto relative overflow-hidden sm:overflow-visible" data-id="element-1997" data-mp="3pg53lj8us364">
          {/* Background Winding Line (CSS fallback for the trail) */}
          <div className="absolute top-32 bottom-32 left-1/2 -translate-x-1/2 w-1.5 bg-slate-200/50 rounded-full z-0" data-id="element-1998" data-mp="2ws0x4vigjm2d" />

          {/* 1. Trailhead */}
          <motion.div variants={itemVariants} className="relative z-10" data-id="element-1999" data-mp="2stjy6z96xhw1">
            <div className="flex justify-center mb-[-20px] relative z-20" data-id="element-2000" data-mp="36sjkw1d57mpk">
              <div className="w-32 h-32" data-id="element-2001" data-mp="31qu8oie6p2zw">
                <DjayIdentity emotion="celebrating" data-id="element-2002" data-mp="31tym86djhb38" />
              </div>
            </div>
            <JourneyTrailhead overallGrowthStage={overallGrowthStage} overallMastery={overallMastery} currentStreak={currentStreak} data-id="element-2003" data-mp="lywp99dc3lg5" />
          </motion.div>

          {/* 2. The Trail */}
          <div className="flex flex-col w-full px-4 sm:px-8 py-8 relative z-10" data-id="element-2004" data-mp="20d993bgk0ge8">
            {trailNodes.map((node, index) => {
          const prevNode = index > 0 ? trailNodes[index - 1] : null;
          const isFirstStop = index === 0;
          return <motion.div key={`node-${index}`} variants={itemVariants} className="flex flex-col w-full" data-id="element-2005" data-mp="1qm5516sf2fy6" data-mpi={index}>
                  {/* Connector from previous node */}
                  {!isFirstStop && node.type === 'stop' && prevNode?.type === 'stop' && <TrailConnector direction={prevNode.align === 'left' ? 'left-to-right' : 'right-to-left'} colorClass={node.data.color} data-id="element-2006" data-mp="17qokgirgqied" />}
                  {/* Straight connector if coming from territory marker */}
                  {!isFirstStop && node.type === 'stop' && prevNode?.type === 'territory' && <TrailConnector direction="straight" colorClass={node.data.color} data-id="element-2007" data-mp="3oc3bs1yhhhmt" />}

                  {/* Node Content */}
                  {node.type === 'territory' && <div className="self-center my-8 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full border-2 border-slate-200 shadow-sm flex items-center gap-2 relative z-10" data-id="element-2008" data-mp="2vopkokhgvgam">
                      <node.data.icon className={`w-5 h-5 ${node.data.accentColor.split(' ')[1]}`} data-id="element-2009" data-mp="35mdixkea75xj" />
                      <span className="font-black text-slate-700 text-sm uppercase tracking-wider" data-id="element-2010" data-mp="kubmwwfn9dsu">
                        {node.data.label}
                      </span>
                    </div>}

                  {node.type === 'stop' && <TrailStop module={node.data} families={getFamiliesForModule(node.data.id)} state={state} selectedGroup={selectedGroup} align={node.align as 'left' | 'right'} data-id="element-2011" data-mp="3mf3e1p5uj3lz" />}

                  {node.type === 'milestone' && <CheckpointFlag label={node.data.label} emoji={node.data.emoji} align={node.align as 'left' | 'right'} data-id="element-2012" data-mp="33oc5uqxlhj96" />}

                  {node.type === 'collectible' && <CollectibleCheckpoint collectible={node.data as CollectibleNodeData} align={node.align as 'left' | 'right'} onOpenCollection={() => onNavigate('collection')} data-id="element-2013" data-mp="2ptt7cqkruzw5" />}
                </motion.div>;
        })}
          </div>

          {/* 2b. Collectible bridge — next unlock teaser */}
          {nextUnlock && <motion.div variants={itemVariants} className="flex flex-col w-full px-4 sm:px-8 pb-8 -mt-4" data-id="element-2014" data-mp="vevy9us3fslk">
              <GhostTrophy variant={nextUnlock} align="center" onOpenCollection={() => onNavigate('collection')} data-id="element-2015" data-mp="1j5rseyk0uzvz" />
            </motion.div>}

          {/* 3. Trail's End (Treasure Chest) */}
          <motion.div variants={itemVariants} data-id="element-2016" data-mp="1e4ue4x24upzr">
            <TreasureChest fastestTime={records.fastestTime} accuracy={records.accuracy} longestStreak={records.longestStreak} perfectSessions={records.perfectSessions} dailyGoals={records.dailyGoals} onOpenCollection={() => onNavigate('collection')} data-id="element-2017" data-mp="1e3kdc03wb3pf" />
          </motion.div>
        </div>}
    </PageContainer>;
}